import React from 'react';
import PropTypes from 'prop-types';
import Block from '../Block';
import Link from './shared/Link';
import fabric from '../../../common/fabric';
import variables from '../../../common/variables';

/**
 * Group component
 *
 * @param props
 * @returns {XML}
 * @constructor
 */
const Group = props => {
  const { appInstanceId, params, style, hyperlink } = props;

  const [groupObjects, setGroupObjects] = React.useState([]);
  const [matrixGroup, setMatrixGroup] = React.useState([]);

  // Unlike standard objects, who are positioned relatively to the canvas left and top edge, an
  // object in a group is positioned relatively to the center of the rectangle formed by the group.
  // We need to adjust the object left and top properties so that they are positioned
  // relatively to the group left and top edge.
  React.useEffect(() => {
    const _group = new fabric.Group();
    _group.setOptions({
      ...params,
    });

    if (params.scaleFactorX) {
      fabric.Group.fromObject(params, g => {
        g._objects.forEach(o => {
          o.scaleObjectInGroup({
            scaleX: params.scaleFactorX,
            scaleY: params.scaleFactorY,
            _by: null,
          });
        });
        setGroupObjects(g._objects.map(o => o.toObject(variables.propsToSave)));
      });
    } else {
      setGroupObjects(
        _group.objects
          ? _group.objects.map(object => ({
              ...object,
            }))
          : [],
      );
    }

    //
    setMatrixGroup(
      props.matrixGroup
        ? fabric.util.multiplyTransformMatrices(
            props.matrixGroup,
            _group.calcTransformMatrix(),
          )
        : _group.calcTransformMatrix(),
    );
  }, [props.params]);
  const blockStyles = {
    ...style,
    top: 0,
    left: 0,
    width: 0,
    height: 0,
    transform: 'none',
    backgroundColor: 'transparent',
    opacity: params.opacity,
  };

  return (
    <Link hyperlink={hyperlink} style={blockStyles}>
      {groupObjects.map((block, index) => (
        <Block
          key={`block-${index}`}
          matrixGroup={matrixGroup}
          currentSlideNumber={props.currentSlideNumber}
          type={block.ludus.type}
          params={block}
          appInstanceId={appInstanceId}
          globalScale={props.globalScale}
          groupScaleX={props.params.scaleX}
          groupScaleY={props.params.scaleY}
          groupAngle={props.groupAngle}
          groupOpacity={props.groupOpacity}
          transitionStatus={props.transitionStatus}
          windowHasFocus={props.windowHasFocus}
          linksEnabled={props.linksEnabled && !hyperlink}
          hasSymbolAsParent={
            props.hasSymbolAsParent || !!props.params.reference
          }
        />
      ))}
    </Link>
  );
};

Group.propTypes = {
  style: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  ).isRequired,
  params: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.array,
      PropTypes.bool,
      PropTypes.func,
      PropTypes.number,
      PropTypes.object,
      PropTypes.string,
    ]),
  ).isRequired,
  appInstanceId: PropTypes.string.isRequired,
  globalScale: PropTypes.number.isRequired,
  groupAngle: PropTypes.number.isRequired,
  matrixGroup: PropTypes.arrayOf(PropTypes.number),
  groupOpacity: PropTypes.number.isRequired,
  transitionStatus: PropTypes.string.isRequired,
  hyperlink: PropTypes.string,
  linksEnabled: PropTypes.bool.isRequired,
  windowHasFocus: PropTypes.bool.isRequired,
  currentSlideNumber: PropTypes.number.isRequired,
  hasSymbolAsParent: PropTypes.bool,
};
Group.defaultProps = {
  hyperlink: null,
  matrixGroup: null,
  hasSymbolAsParent: false,
};

export default Group;
